import { message as antdMessage, Button, Input, Popconfirm, Space } from 'antd';
import { RobotOutlined, SendOutlined } from '@ant-design/icons';
import { useState } from 'react';
import axios from 'axios';
import { useAuthenticatedRequest } from '../UserContext';

interface ChatInputProps {
  onSend: (message: string) => void;
  loading?: boolean;
  disabled?: boolean;
  engagementId?: string;
  apiUrl?: string;
  accessToken?: string;
  confirmSend?: boolean;
}

export const ChatInput = ({
  onSend,
  loading = false,
  disabled = false,
  engagementId,
  apiUrl,
  accessToken,
  confirmSend = false,
}: ChatInputProps) => {
  const authenticatedRequest = useAuthenticatedRequest(accessToken);
  const [message, setMessage] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const generateAIMessage = async () => {
    if (!engagementId || !apiUrl || !message.trim()) {
      return;
    }

    setAiLoading(true);
    try {
      const response = await authenticatedRequest(headers =>
        axios.post(
          `${apiUrl}/api/lead-engagement/${engagementId}/generate-message`,
          { message },
          { headers },
        ),
      );

      if (response.data && response.data.message) {
        setMessage(response.data.message);
      }
    } catch (error) {
      console.error('Failed to generate AI message:', error);
      antdMessage.error('Failed to generate AI message');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSend = () => {
    if (!message.trim()) {
      return;
    }

    onSend(message);
    setMessage('');
  };

  const sendButton = (
    <Button
      disabled={disabled || !message.trim()}
      icon={<SendOutlined />}
      loading={loading}
      onClick={confirmSend ? undefined : handleSend}
      type="primary"
    >
      Send
    </Button>
  );

  return (
    <div
      style={{
        padding: '16px',
        borderTop: '1px solid #f0f0f0',
        backgroundColor: 'white',
      }}
    >
      <Space.Compact style={{ width: '100%' }}>
        <Button
          disabled={disabled || !engagementId || !message.trim()}
          icon={<RobotOutlined />}
          loading={aiLoading}
          onClick={generateAIMessage}
          style={{ marginRight: 8 }}
          title="Rewrite with AI"
        />
        <Input
          disabled={disabled || loading}
          onChange={e => setMessage(e.target.value)}
          onPressEnter={confirmSend ? undefined : handleSend}
          placeholder="Type a message..."
          value={message}
        />
        {confirmSend ? (
          <Popconfirm
            cancelText="No"
            description="Are you sure you want to send this message to the lead?"
            okText="Send"
            onConfirm={handleSend}
            title="Send message"
          >
            {sendButton}
          </Popconfirm>
        ) : (
          sendButton
        )}
      </Space.Compact>
    </div>
  );
};
